/**
 * Offline Storage Utility
 * Uses IndexedDB for persistent local storage with sync queue
 */

const DB_NAME = 'mipay_offline_db';
const DB_VERSION = 1;

let db = null;

// Initialize IndexedDB
const initDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }
    
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };
    
    request.onupgradeneeded = (event) => {
      const database = event.target.result;
      
      // NAS stores
      if (!database.objectStoreNames.contains('nas_products')) {
        database.createObjectStore('nas_products', { keyPath: 'id' });
      }
      if (!database.objectStoreNames.contains('nas_orders')) {
        database.createObjectStore('nas_orders', { keyPath: 'id' });
      }
      if (!database.objectStoreNames.contains('nas_bodega')) {
        database.createObjectStore('nas_bodega', { keyPath: 'id' });
      }
      if (!database.objectStoreNames.contains('nas_shop_transactions')) {
        database.createObjectStore('nas_shop_transactions', { keyPath: 'id' });
      }
      if (!database.objectStoreNames.contains('nas_deliveries')) {
        database.createObjectStore('nas_deliveries', { keyPath: 'id' });
      }
      
      // Sync queue
      if (!database.objectStoreNames.contains('sync_queue')) {
        const syncStore = database.createObjectStore('sync_queue', { keyPath: 'id', autoIncrement: true });
        syncStore.createIndex('timestamp', 'timestamp');
        syncStore.createIndex('type', 'type');
      }
      
      // ARBE stores (for existing Mipay features)
      if (!database.objectStoreNames.contains('arbe_timesheets')) {
        database.createObjectStore('arbe_timesheets', { keyPath: 'id' });
      }
      if (!database.objectStoreNames.contains('arbe_payslips')) {
        database.createObjectStore('arbe_payslips', { keyPath: 'id' });
      }
      if (!database.objectStoreNames.contains('arbe_cash_advances')) {
        database.createObjectStore('arbe_cash_advances', { keyPath: 'id' });
      }
    };
  });
};

// Save data to a specific store
export const saveToLocalDB = async (storeName, data) => {
  try {
    const database = await initDB();
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    
    // Clear existing data and add new
    store.clear();
    
    if (Array.isArray(data)) {
      data.forEach(item => store.add(item));
    } else {
      store.add(data);
    }
    
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve(true);
      transaction.onerror = () => reject(transaction.error);
    });
  } catch (error) {
    console.error('Error saving to IndexedDB:', error);
    // Fallback to localStorage
    localStorage.setItem(storeName, JSON.stringify(data));
    return true;
  }
};

// Get data from a specific store
export const getFromLocalDB = async (storeName) => {
  try {
    const database = await initDB();
    const transaction = database.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();
    
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error reading from IndexedDB:', error);
    // Fallback to localStorage
    const data = localStorage.getItem(storeName);
    return data ? JSON.parse(data) : [];
  }
};

// Get single item by ID
export const getItemFromLocalDB = async (storeName, id) => {
  try {
    const database = await initDB();
    const transaction = database.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(id);
    
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error reading item from IndexedDB:', error);
    return null;
  }
};

// Update single item
export const updateInLocalDB = async (storeName, data) => {
  try {
    const database = await initDB();
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    store.put(data);
    
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve(true);
      transaction.onerror = () => reject(transaction.error);
    });
  } catch (error) {
    console.error('Error updating IndexedDB:', error);
    return false;
  }
};

// Delete single item
export const deleteFromLocalDB = async (storeName, id) => {
  try {
    const database = await initDB();
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    store.delete(id);
    
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve(true);
      transaction.onerror = () => reject(transaction.error);
    });
  } catch (error) {
    console.error('Error deleting from IndexedDB:', error);
    return false;
  }
};

// Add to sync queue
export const addToSyncQueue = async (action) => {
  try {
    const database = await initDB();
    const transaction = database.transaction('sync_queue', 'readwrite');
    const store = transaction.objectStore('sync_queue');
    
    store.add({
      ...action,
      id: Date.now(),
      timestamp: new Date().toISOString(),
      synced: false
    });
    
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => {
        // Update pending count in localStorage for quick access
        updatePendingSyncCount();
        resolve(true);
      };
      transaction.onerror = () => reject(transaction.error);
    });
  } catch (error) {
    console.error('Error adding to sync queue:', error);
    // Fallback to localStorage
    const queue = JSON.parse(localStorage.getItem('sync_queue') || '[]');
    queue.push({ ...action, timestamp: new Date().toISOString() });
    localStorage.setItem('sync_queue', JSON.stringify(queue));
    return true;
  }
};

// Get sync queue
export const getSyncQueue = async () => {
  try {
    const database = await initDB();
    const transaction = database.transaction('sync_queue', 'readonly');
    const store = transaction.objectStore('sync_queue');
    const request = store.getAll();
    
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result.filter(item => !item.synced));
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error reading sync queue:', error);
    const queue = JSON.parse(localStorage.getItem('sync_queue') || '[]');
    return queue;
  }
};

// Clear synced items from queue
export const clearSyncedItems = async (ids) => {
  try {
    const database = await initDB();
    const transaction = database.transaction('sync_queue', 'readwrite');
    const store = transaction.objectStore('sync_queue');
    
    ids.forEach(id => store.delete(id));
    
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => {
        updatePendingSyncCount();
        resolve(true);
      };
      transaction.onerror = () => reject(transaction.error);
    });
  } catch (error) {
    console.error('Error clearing synced items:', error);
    return false;
  }
};

// Update pending sync count
const updatePendingSyncCount = async () => {
  const queue = await getSyncQueue();
  localStorage.setItem('pending_sync_count', queue.length.toString());
};

// Process sync queue when online
export const processSyncQueue = async (apiUrl, token) => {
  if (!navigator.onLine) return { success: false, message: 'Offline' };
  
  const queue = await getSyncQueue();
  if (queue.length === 0) return { success: true, message: 'Nothing to sync' };
  
  const syncedIds = [];
  const errors = [];
  
  for (const item of queue) {
    try {
      const endpoint = getSyncEndpoint(item.type);
      const method = getSyncMethod(item.type);
      
      const response = await fetch(`${apiUrl}${endpoint}`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(item.data)
      });
      
      if (response.ok) {
        syncedIds.push(item.id);
      } else {
        errors.push({ id: item.id, error: await response.text() });
      }
    } catch (error) {
      errors.push({ id: item.id, error: error.message });
    }
  }
  
  if (syncedIds.length > 0) {
    await clearSyncedItems(syncedIds);
  }
  
  return {
    success: errors.length === 0,
    synced: syncedIds.length,
    failed: errors.length,
    errors
  };
};

// Get API endpoint for sync action
const getSyncEndpoint = (type) => {
  const endpoints = {
    // NAS endpoints
    'CREATE_PRODUCT': '/api/nas/products',
    'UPDATE_PRODUCT': '/api/nas/products',
    'DELETE_PRODUCT': '/api/nas/products',
    'CREATE_ORDER': '/api/nas/orders',
    'UPDATE_ORDER': '/api/nas/orders',
    'CREATE_DELIVERY': '/api/nas/deliveries',
    'RECORD_SALE': '/api/nas/sales',
    'TRANSFER_STOCK': '/api/nas/stock/transfer',
    // ARBE endpoints
    'CREATE_TIMESHEET': '/api/timesheets-v2',
    'UPDATE_TIMESHEET': '/api/timesheets-v2',
    'CREATE_CASH_ADVANCE': '/api/cash-advance/request',
  };
  return endpoints[type] || '/api/sync';
};

// Get HTTP method for sync action
const getSyncMethod = (type) => {
  if (type.startsWith('CREATE_') || type.startsWith('RECORD_')) return 'POST';
  if (type.startsWith('UPDATE_') || type.startsWith('TRANSFER_')) return 'PUT';
  if (type.startsWith('DELETE_')) return 'DELETE';
  return 'POST';
};

export default {
  saveToLocalDB,
  getFromLocalDB,
  getItemFromLocalDB,
  updateInLocalDB,
  deleteFromLocalDB,
  addToSyncQueue,
  getSyncQueue,
  clearSyncedItems,
  processSyncQueue
};
